import { Link } from "@/lib/nav";

import { Button } from "@buildpy/ui/button";
import EmptyState from "@buildpy/ui/empty-state";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import ListRowCard, { ListRows } from "@/components/list-card";
import type { StaffTodaySite } from "@buildpy/core/modules/project/staff-today";
import { dDayLabel } from "@buildpy/core/modules/project/view";
import type { StaffTodayView } from "@buildpy/core/views/g3a/staff-today";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";

const mmdd = (date: string): string => date.slice(5);

const projectHref = (id: string): string => `/app/projects/${encodeURIComponent(id)}`;

// 「사진 0장」 은 오늘 한 장도 없을 때만 — 장 수는 읽지 않아 있으면 「사진 있음」, 확인 못 하면 뺀다.
const photoText = (site: StaffTodaySite): string | null => {
  if (site.status !== "IN_PROGRESS" || site.photoToday === null) return null;
  return site.photoToday ? "사진 있음" : "사진 0장";
};

const SiteCard = ({ site }: { site: StaffTodaySite }) => (
  <ListRowCard
    badge={<ProjectStatusBadge status={site.status} />}
    href={projectHref(site.id)}
    meta={[
      site.address,
      site.startDate && `착공 ${mmdd(site.startDate)}`,
      site.dDay !== null && dDayLabel(site.dDay),
      photoText(site),
      site.status === "IN_PROGRESS" && site.logged && "일지 씀",
    ]}
    subtitle={
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>
          <span className="text-muted-foreground">오늘 공정</span> {site.phaseText ?? "—"}
        </span>
        {site.gap && <Pill tone="danger">{site.gap}</Pill>}
      </span>
    }
    testId="staff-today-site"
    title={site.name}
  />
);

// 직원 앱 「오늘」 — 날짜 · 어제 빠진 일지 · 오늘 나가는 현장 · 이번 주 일정(시안 Main).
const StaffTodayPage = ({ data }: { data: StaffTodayView }) => {
  const { dateLabel, memberName, missed, sites, week } = data;
  const [firstMissed] = missed;
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-y-5" data-testid="staff-today">
      <header className="flex items-center justify-between gap-x-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{dateLabel}</p>
          <h1 className="text-3xl font-bold">오늘</h1>
        </div>
        <span
          aria-hidden
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-lg font-semibold text-accent-foreground">
          {memberName.slice(0, 1)}
        </span>
      </header>

      {firstMissed && (
        <SectionCard className="flex items-center justify-between gap-x-3" data-testid="staff-today-missed" padding="sm">
          <div className="min-w-0">
            <p className="font-semibold">어제 현장일지를 안 쓴 현장이 {missed.length}곳 있어요</p>
            <p className="truncate text-sm text-muted-foreground">
              {firstMissed.name} · {mmdd(firstMissed.date)}
            </p>
          </div>
          <Button asChild className="shrink-0" size="sm">
            <Link href={`${projectHref(firstMissed.id)}/today?date=${firstMissed.date}`}>지금 쓰기</Link>
          </Button>
        </SectionCard>
      )}

      <section aria-labelledby="staff-today-sites" className="flex flex-col gap-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold" id="staff-today-sites">
            오늘 나가는 현장
          </h2>
          <Button asChild size="xs-touch" variant="link-muted">
            <Link href="/app/projects">전체 보기</Link>
          </Button>
        </div>
        {sites.length > 0 ? (
          <ul aria-label="오늘 나가는 현장" className="flex min-w-0 flex-col gap-y-2">
            {sites.map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </ul>
        ) : (
          <EmptyState>오늘 나가는 현장이 없어요</EmptyState>
        )}
      </section>

      <SectionCard aria-labelledby="staff-today-week" padding="sm">
        <h2 className="text-lg font-semibold" id="staff-today-week">
          이번 주 일정
        </h2>
        {week.length > 0 ? (
          <ListRows label="이번 주 일정">
            {week.map((ev) => (
              <li className="flex items-center gap-x-3 py-2.5 text-sm" key={`${ev.date}-${ev.siteId}-${ev.title}`}>
                <span className="flex w-10 shrink-0 flex-col items-center tabular-nums">
                  <span className="text-xs text-muted-foreground">{ev.dow}</span>
                  <span className="font-semibold">{mmdd(ev.date)}</span>
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{ev.title}</span>
                  <Link className="block truncate text-xs text-muted-foreground hover:underline" href={projectHref(ev.siteId)}>
                    {ev.siteName}
                  </Link>
                </span>
              </li>
            ))}
          </ListRows>
        ) : (
          <EmptyState variant="plain">이번 주 잡힌 일정이 없어요</EmptyState>
        )}
      </SectionCard>
    </div>
  );
};

export default StaffTodayPage;
